import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Pencil, RefreshCw, ShieldAlert, X } from 'lucide-react';
import { listAllAppVersions } from '../../api/appVersion';
import { getAdminDirectoryUsersByIds } from '../../api/adminDirectory';
import {
  listPushDeliveryPolicies,
  listPushDeliveryPolicyAudits,
  updatePushDeliveryPolicy,
} from '../../api/pushAdmin';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { API_ENV_TEST, getSelectedEnvironment } from '../../utils/apiEnvironment';
import { t } from '../../utils/i18n';
import { DM_PUSH_TYPES } from '../../api/dmPushAdmin';

export function registeredVersionNames(rows, platform) {
  return [...new Set((rows || [])
    .filter((row) => row.platform === platform && !row.deleted_at)
    .map((row) => row.version_name)
    .filter(Boolean))].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
}

export function dmVersionPolicyValid(policy, enabled, versions, environment) {
  if (environment === 'PROD' && DM_PUSH_TYPES.includes(policy.notification_type)) return versions[0] === '1.9.11' && versions[1] === '1.9.11' && !versions[2] && !versions[3];
  if (!policy.capabilities?.minimum_version_required) return true;
  return (!enabled || Boolean(versions[0] || versions[1])) && versions.every(value => !value || /^\d+\.\d+\.\d+$/.test(value));
}

export function productionConfirmPhrase(notificationType) {
  return `${notificationType} PROD`;
}

function versionParts(value) {
  return String(value || '').replace(/^[vV]/, '').split(/[+-]/, 1)[0].split('.').map(Number);
}

export function versionRangeValid(minimum, maximum) {
  if (!minimum || !maximum) return true;
  const left = versionParts(minimum);
  const right = versionParts(maximum);
  for (let index = 0; index < 3; index += 1) {
    const a = left[index] || 0;
    const b = right[index] || 0;
    if (a !== b) return a < b;
  }
  return true;
}

function dateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function surfaceLabel(surface) {
  return t(`push.deliveryPolicies.surface.${surface || 'UNCLASSIFIED'}`);
}

function auditState(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function canPreserveUnregisteredVersion(value, originalValue, enabled) {
  return !enabled && value === originalValue && /^\d+\.\d+\.\d+$/.test(value || '');
}

function VersionSelect({ label, value, originalValue, enabled, versions, onChange }) {
  const missing = value && !versions.includes(value);
  const canPreserve = canPreserveUnregisteredVersion(value, originalValue, enabled);
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">{label}</span>
      <select
        value={value || ''}
        onChange={(event) => onChange(event.target.value || null)}
        className="w-full rounded-lg border border-white/15 bg-dark-bg px-3 py-2"
      >
        <option value="">{t('push.deliveryPolicies.unrestricted')}</option>
        {missing && <option value={value}>{value} ({t('push.deliveryPolicies.unregistered')})</option>}
        {versions.map((version) => <option key={version} value={version}>{version}</option>)}
      </select>
      {missing && <span className="mt-1 block text-xs text-amber-300">{t(`push.deliveryPolicies.${canPreserve ? 'unregisteredPreservedHint' : 'unregisteredHint'}`)}</span>}
    </label>
  );
}

export function AuditPanel({ policy, token, onClose, environment }) {
  const query = useQuery({
    queryKey: ['pushDeliveryPolicyAudits', environment, policy.notification_type],
    queryFn: () => listPushDeliveryPolicyAudits(policy.notification_type, token),
  });
  const actorIds = (query.data?.items || []).map((audit) => audit.admin_user_id);
  const actorsQuery = useQuery({
    queryKey: ['pushDeliveryPolicyAuditActors', environment, ...actorIds],
    queryFn: () => getAdminDirectoryUsersByIds(actorIds, token),
    enabled: query.isSuccess && actorIds.length > 0,
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/65" role="dialog" aria-modal="true">
      <section className="h-full w-full max-w-xl overflow-y-auto border-l border-white/10 bg-dark-surface p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div><h2 className="text-xl font-bold">{t('push.deliveryPolicies.auditTitle')}</h2><code className="text-sm text-primary">{policy.notification_type}</code></div>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')}><X /></button>
        </div>
        {query.isLoading && <p className="mt-6 text-dark-text-secondary">{t('common.loading')}</p>}
        {query.isError && <p className="mt-6 text-red-300">{query.error.message}</p>}
        <div className="mt-6 space-y-3">
          {(query.data?.items || []).map((audit) => {
            const before = auditState(audit.before_state);
            const after = auditState(audit.after_state);
            return <article key={audit.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex justify-between gap-3 text-xs text-dark-text-secondary"><span>{audit.environment} · {actorsQuery.data?.[audit.admin_user_id]?.username || `Admin #${audit.admin_user_id}`}</span><time>{dateTime(audit.created_at)}</time></div>
              <p className="mt-2 text-sm">{audit.reason}</p>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-dark-text-secondary">
                <dt>{t('push.deliveryPolicies.enabled')}</dt><dd>{before.enabled ? 'ON' : 'OFF'} → {after.enabled ? 'ON' : 'OFF'}</dd>
                <dt>{t('push.deliveryPolicies.minIOS')}</dt><dd>{before.min_ios_version || '-'} → {after.min_ios_version || '-'}</dd>
                <dt>{t('push.deliveryPolicies.minAndroid')}</dt><dd>{before.min_android_version || '-'} → {after.min_android_version || '-'}</dd>
                <dt>{t('push.deliveryPolicies.maxIOS')}</dt><dd>{before.max_ios_version || '-'} → {after.max_ios_version || '-'}</dd>
                <dt>{t('push.deliveryPolicies.maxAndroid')}</dt><dd>{before.max_android_version || '-'} → {after.max_android_version || '-'}</dd>
              </dl>
            </article>;
          })}
          {query.isSuccess && (query.data?.items || []).length === 0 && <p className="text-dark-text-secondary">{t('push.deliveryPolicies.auditEmpty')}</p>}
        </div>
      </section>
    </div>
  );
}

export function EditDialog({ policy, versions, environment, token, onClose, onSaved }) {
  const { registerEnvironmentGuard } = useApiEnvironment();
  const [enabled, setEnabled] = useState(Boolean(policy.enabled));
  const [minIOSVersion, setMinIOSVersion] = useState(policy.min_ios_version || null);
  const [minAndroidVersion, setMinAndroidVersion] = useState(policy.min_android_version || null);
  const [maxIOSVersion, setMaxIOSVersion] = useState(policy.max_ios_version || null);
  const [maxAndroidVersion, setMaxAndroidVersion] = useState(policy.max_android_version || null);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const iosVersions = useMemo(() => registeredVersionNames(versions, 'IOS'), [versions]);
  const androidVersions = useMemo(() => registeredVersionNames(versions, 'ANDROID'), [versions]);
  const phrase = productionConfirmPhrase(policy.notification_type);
  const isProd = environment === 'PROD';
  const dmVersionsValid = dmVersionPolicyValid(policy, enabled, [minIOSVersion, minAndroidVersion, maxIOSVersion, maxAndroidVersion], environment);
  const versionAllowed = (value, originalValue, registered) => !value || registered.includes(value)
    || canPreserveUnregisteredVersion(value, originalValue, enabled);
  const versionsValid = dmVersionsValid && versionAllowed(minIOSVersion, policy.min_ios_version, iosVersions)
    && versionAllowed(minAndroidVersion, policy.min_android_version, androidVersions)
    && versionAllowed(maxIOSVersion, policy.max_ios_version, iosVersions)
    && versionAllowed(maxAndroidVersion, policy.max_android_version, androidVersions)
    && versionRangeValid(minIOSVersion, maxIOSVersion)
    && versionRangeValid(minAndroidVersion, maxAndroidVersion);
  const mutation = useMutation({
    mutationFn: () => {
      if (!dmVersionsValid) throw new Error(t('push.deliveryPolicies.dmMinimumRequired'));
      if (getSelectedEnvironment().toUpperCase() !== environment) throw new Error(t('push.operations.environmentChanged'));
      return updatePushDeliveryPolicy(policy.notification_type, {
      environment,
      ...(isProd && DM_PUSH_TYPES.includes(policy.notification_type) ? { confirmation } : {}),
      enabled,
      min_ios_version: minIOSVersion,
      min_android_version: minAndroidVersion,
      max_ios_version: maxIOSVersion,
      max_android_version: maxAndroidVersion,
      reason: reason.trim(),
      expected_updated_at: policy.updated_at,
    }, token);
    },
    onSuccess: onSaved,
  });
  useEffect(() => registerEnvironmentGuard?.(() => !mutation.isPending), [registerEnvironmentGuard, mutation.isPending]);
  const canSubmit = versionsValid && reason.trim().length >= 5
    && (!isProd || confirmation === phrase) && !mutation.isPending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
      <form onSubmit={(event) => { event.preventDefault(); if (canSubmit) mutation.mutate(); }} className="w-full max-w-2xl rounded-2xl border border-white/10 bg-dark-surface p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4"><div><h2 className="text-xl font-bold">{t('push.deliveryPolicies.editTitle')}</h2><code className="text-sm text-primary">{policy.notification_type}</code></div><button type="button" onClick={onClose}><X /></button></div>
        <div className={`mt-5 rounded-xl border p-4 ${isProd ? 'border-red-400/50 bg-red-950/20 text-red-100' : 'border-amber-400/40 bg-amber-950/20 text-amber-100'}`}>
          <strong>{environment}</strong> · {t(`push.deliveryPolicies.${isProd ? 'prodWarning' : 'testWarning'}`)}
        </div>
        <label className="mt-5 flex items-center justify-between rounded-xl border border-white/10 p-4"><span><strong>{t('push.deliveryPolicies.enabled')}</strong><small className="mt-1 block text-dark-text-secondary">{t('push.deliveryPolicies.enabledHint')}</small></span><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} className="h-5 w-5" /></label>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <VersionSelect label={t('push.deliveryPolicies.minIOS')} value={minIOSVersion} originalValue={policy.min_ios_version} enabled={enabled} versions={iosVersions} onChange={setMinIOSVersion} />
          <VersionSelect label={t('push.deliveryPolicies.minAndroid')} value={minAndroidVersion} originalValue={policy.min_android_version} enabled={enabled} versions={androidVersions} onChange={setMinAndroidVersion} />
          <VersionSelect label={t('push.deliveryPolicies.maxIOS')} value={maxIOSVersion} originalValue={policy.max_ios_version} enabled={enabled} versions={iosVersions} onChange={setMaxIOSVersion} />
          <VersionSelect label={t('push.deliveryPolicies.maxAndroid')} value={maxAndroidVersion} originalValue={policy.max_android_version} enabled={enabled} versions={androidVersions} onChange={setMaxAndroidVersion} />
        </div>
        {policy.capabilities?.minimum_version_required && <p className="mt-3 text-sm text-amber-200">{t('push.deliveryPolicies.dmMinimumRequired')}</p>}
        {isProd && DM_PUSH_TYPES.includes(policy.notification_type) && <p className="mt-3 text-sm text-amber-200">{t('push.deliveryPolicies.dmProductionVersions')}</p>}
        {!versionsValid && <p className="mt-3 text-sm text-red-300">{t('push.deliveryPolicies.invalidRange')}</p>}
        <label className="mt-5 block text-sm"><span className="mb-1 block font-medium">{t('push.deliveryPolicies.reason')}</span><textarea required minLength={5} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} className="h-24 w-full rounded-lg border border-white/15 bg-dark-bg p-3" placeholder={t('push.deliveryPolicies.reasonPlaceholder')} /></label>
        {isProd && <label className="mt-4 block text-sm"><span className="mb-1 block text-red-200">{t('push.deliveryPolicies.prodConfirm', { phrase })}</span><input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} className="w-full rounded-lg border border-red-400/40 bg-dark-bg px-3 py-2" /></label>}
        {mutation.isError && <p className="mt-4 text-sm text-red-300">{mutation.error.message}</p>}
        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={onClose} className="rounded-lg border border-white/20 px-4 py-2">{t('common.cancel')}</button><button disabled={!canSubmit} className="rounded-lg bg-primary px-4 py-2 font-semibold text-black disabled:opacity-40">{t('push.deliveryPolicies.save')}</button></div>
      </form>
    </div>
  );
}

export default function PushDeliveryPolicies() {
  const { token, role } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const queryClient = useQueryClient();
  const environment = getSelectedEnvironment() === API_ENV_TEST ? 'TEST' : 'PROD';
  const [editing, setEditing] = useState(null);
  const [auditing, setAuditing] = useState(null);
  const query = useQuery({ queryKey: ['pushDeliveryPolicies', apiEnvironment], queryFn: () => listPushDeliveryPolicies(token) });
  const versionsQuery = useQuery({ queryKey: ['allAppVersions', apiEnvironment], queryFn: listAllAppVersions });
  const items = query.data?.items || [];
  const actorIds = items.map((policy) => policy.updated_by);
  const actorsQuery = useQuery({
    queryKey: ['pushDeliveryPolicyActors', apiEnvironment, ...actorIds],
    queryFn: () => getAdminDirectoryUsersByIds(actorIds, token),
    enabled: query.isSuccess && actorIds.some(Boolean),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pushDeliveryPolicies', apiEnvironment] });
  return (
    <main className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4"><div><h1 className="text-2xl font-bold">{t('push.deliveryPolicies.title')}</h1><p className="mt-1 text-dark-text-secondary">{t('push.deliveryPolicies.description')}</p></div><button type="button" onClick={refresh} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2"><RefreshCw size={16} />{t('push.deliveryPolicies.refresh')}</button></header>
      <section className={`rounded-xl border p-4 ${environment === 'PROD' ? 'border-red-400/40 bg-red-950/20' : 'border-amber-400/40 bg-amber-950/20'}`}><div className="flex items-start gap-3"><ShieldAlert className={environment === 'PROD' ? 'text-red-300' : 'text-amber-300'} /><div><strong>{environment}</strong><p className="text-sm text-dark-text-secondary">{t('push.deliveryPolicies.environmentHint')}</p></div></div></section>
      <section className="overflow-x-auto rounded-xl border border-white/10 bg-dark-surface">
        <table className="w-full min-w-[1050px] text-left text-sm"><thead><tr className="border-b border-white/10 text-xs uppercase text-dark-text-secondary"><th className="p-4">{t('push.deliveryPolicies.type')}</th><th>{t('push.deliveryPolicies.surfaceLabel')}</th><th>{t('push.deliveryPolicies.enabled')}</th><th>{t('push.deliveryPolicies.iosRange')}</th><th>{t('push.deliveryPolicies.androidRange')}</th><th>{t('push.deliveryPolicies.lastChange')}</th><th className="pr-4 text-right">{t('push.deliveryPolicies.actions')}</th></tr></thead>
          <tbody>{items.map((policy) => <tr key={policy.notification_type} className="border-b border-white/5 align-top"><td className="p-4"><strong>{policy.display_name}</strong><code className="mt-1 block text-xs text-primary">{policy.notification_type} · Inbox {policy.inbox_type}</code>{!policy.mutable && <span className="mt-2 inline-block rounded bg-red-500/15 px-2 py-1 text-xs text-red-200">{t('push.deliveryPolicies.failClosed')}</span>}</td><td className="py-4"><span className="rounded-full bg-white/10 px-2 py-1 text-xs">{surfaceLabel(policy.delivery_surface)}</span></td><td className="py-4"><span className={`rounded-full px-2 py-1 text-xs ${policy.enabled ? 'bg-emerald-500/15 text-emerald-200' : 'bg-red-500/15 text-red-200'}`}>{policy.enabled ? 'ON' : 'OFF'}</span></td><td className="py-4">{policy.min_ios_version || '−∞'} – {policy.max_ios_version || '+∞'}</td><td className="py-4">{policy.min_android_version || '−∞'} – {policy.max_android_version || '+∞'}</td><td className="max-w-xs py-4"><span>{actorsQuery.data?.[policy.updated_by]?.username || (policy.updated_by ? `Admin #${policy.updated_by}` : '-')}</span><time className="block text-xs text-dark-text-secondary">{dateTime(policy.updated_at)}</time><small className="mt-1 block truncate text-dark-text-secondary" title={policy.last_reason}>{policy.last_reason || '-'}</small></td><td className="py-4 pr-4"><div className="flex justify-end gap-2"><button type="button" onClick={() => setAuditing(policy)} className="rounded border border-white/15 p-2" title={t('push.deliveryPolicies.auditTitle')}><History size={16} /></button><button type="button" disabled={role !== 'admin' || !policy.mutable} onClick={() => setEditing(policy)} className="rounded border border-primary/50 p-2 text-primary disabled:opacity-30" title={t('push.deliveryPolicies.editTitle')}><Pencil size={16} /></button></div></td></tr>)}</tbody>
        </table>
        {query.isLoading && <p className="p-6 text-dark-text-secondary">{t('common.loading')}</p>}
        {query.isError && <p className="p-6 text-red-300">{query.error.message}</p>}
        {query.isSuccess && items.length === 0 && <p className="p-6 text-dark-text-secondary">{t('push.deliveryPolicies.empty')}</p>}
      </section>
      <p className="text-xs text-dark-text-secondary">{t('push.deliveryPolicies.versionPolicyBoundary')}</p>
      {editing && <EditDialog policy={editing} versions={versionsQuery.data || []} environment={environment} token={token} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {auditing && <AuditPanel policy={auditing} token={token} onClose={() => setAuditing(null)} />}
    </main>
  );
}
